import {
  Alert,
  AlertIcon,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { NeedStatus, useLinkMarketDialogueToNeedMutation, useListNeedsQuery } from "../../autogen/bff-api";
import { useApiError } from "../../common/errors/useApiError";

interface Props {
  marketDialogueId: string;
  marketDialogueTitle: string;
  onClose: () => void;
}

export const LinkNeedDialog = ({ marketDialogueId, marketDialogueTitle, onClose }: Props) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const { data, isLoading, isError, refetch } = useListNeedsQuery({
    status: [NeedStatus.Approved, NeedStatus.Planned].join(","),
  });
  const [link, { isLoading: isLinking }] = useLinkMarketDialogueToNeedMutation();
  const [needId, setNeedId] = useState("");
  const needs = (data?.needs ?? []).filter((need) => need.canStartMarketDialogue);

  const confirm = async () => {
    try {
      await link({ marketDialogueId, linkMarketDialogueNeedRequest: { needId } }).unwrap();
      onClose();
    } catch (e) {
      trigger(e);
    }
  };

  return (
    <Modal isOpen onClose={() => !isLinking && onClose()} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t("Link to a need")}</ModalHeader>
        <ModalCloseButton isDisabled={isLinking} />
        <ModalBody>
          <Text mb="4" color="smMuted">
            {t("Choose the need the market dialogue «{{title}}» is for.", {
              title: marketDialogueTitle.trim() || t("Untitled"),
            })}
          </Text>
          {isLoading && <Spinner size="sm" />}
          {isError && (
            <Alert status="error" rounded="md" fontSize="sm">
              <AlertIcon />
              {t("Could not load the needs.")}
              <Button size="sm" variant="link" ml="2" onClick={() => void refetch()}>
                {t("Try again")}
              </Button>
            </Alert>
          )}
          {data && needs.length === 0 && (
            <Text>{t("No needs with procurement can be linked. A need can be linked once it is approved or planned.")}</Text>
          )}
          {needs.length > 0 && (
            <FormControl isRequired>
              <FormLabel fontSize="sm">{t("Need")}</FormLabel>
              <Select
                placeholder={t("Choose a need") ?? undefined}
                value={needId}
                onChange={(e) => setNeedId(e.target.value)}
              >
                {needs.map((need) => (
                  <option key={need.id} value={need.id}>
                    {need.title?.trim() || t("Untitled")}
                  </option>
                ))}
              </Select>
              <FormHelperText>{t("Only needs that are approved or planned are listed.")}</FormHelperText>
            </FormControl>
          )}
        </ModalBody>
        <ModalFooter gap="3">
          <Button variant="ghost" onClick={onClose} isDisabled={isLinking}>
            {t("Cancel")}
          </Button>
          <Button colorScheme="teal" isDisabled={!needId} isLoading={isLinking} onClick={() => void confirm()}>
            {t("Link the need")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
